// client/src/contexts/SocketContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import { createSocket } from "../lib/socket";

let sharedSocket: Socket | null = null;
let mountCount = 0;

// Use the centralized socket creation function
const createSharedSocket = () => createSocket();

type SocketValue = Socket | null;
export const SocketContext = createContext<SocketValue>(null);
export const useSocket = () => useContext(SocketContext);

export const useSocketStatus = () => {
  const socket = useSocket();
  const [connected, setConnected] = useState<boolean>(!!socket?.connected);
  const [id, setId] = useState<string | null>(socket?.id ?? null);

  useEffect(() => {
    if (!socket) {
      setConnected(false);
      setId(null);
      return;
    }
    const onConnect = () => { setConnected(true); setId(socket.id ?? null); };
    const onDisconnect = () => setConnected(false);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    if (socket.connected) onConnect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [socket]);

  return { connected, id };
};

type ProviderProps = { children: React.ReactNode };

export const SocketProvider: React.FC<ProviderProps> = ({ children }) => {
  // Ensure single instance across StrictMode mounts
  if (!sharedSocket) {
    sharedSocket = createSharedSocket();
    // Optional debug
    sharedSocket.io.on("open", () =>
      console.log("[SOCKET] transport:", sharedSocket!.io.engine.transport.name)
    );
    sharedSocket.on("connect", () =>
      console.log("[SOCKET] connect", sharedSocket!.id)
    );
    sharedSocket.on("connect_error", (e) =>
      console.error("[SOCKET] connect_error", e)
    );
  }
  mountCount++;

  useEffect(() => {
    return () => {
      // Only actually close when the LAST consumer unmounts
      mountCount--;
      if (mountCount <= 0 && sharedSocket) {
        try {
          sharedSocket.removeAllListeners();
          sharedSocket.close();
        } finally {
          sharedSocket = null;
          mountCount = 0;
        }
      }
    };
  }, []);

  return (
    <SocketContext.Provider value={sharedSocket}>
      {children}
    </SocketContext.Provider>
  );
};
